import * as React from "react";
import { styled } from "@mui/material/styles";
import { Backdrop, Grid } from "@mui/material";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { ReactComponent as MenuSvg } from "assets/images/icons/menu.svg";
import MDButton from "components/MDButton";
import { ReactComponent as ExitIcon } from "assets/images/icons/exit.svg";
import { useTranslation } from "react-i18next";
import MDList from "components/MDList";
import { useMaterialUIController, setMiniSidenav,setOpenSidenav } from "context";

// Material Dashboard 2 React base styles
import breakpoints from "assets/theme/base/breakpoints";
import pxToRem from "assets/theme/functions/pxToRem";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
  [theme.breakpoints.down("sm")]: {
    width: pxToRem(0),
  },

  [theme.breakpoints.up("sm")]: {
    width: pxToRem(0),
  },
  [theme.breakpoints.up("lg")]: {
    width: pxToRem(100),
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: "100px",
  position: "absolute",
  left: 0,
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",

    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
  })
);

export default function MiniDrawer({ color, brand, brandName, routes, ...rest }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(true);
  const [controller, dispatch] = useMaterialUIController();
  const { openLeftSidenav,openSidenav} = controller;
  const [isDisplayed, setIsDisplayed] = React.useState(true);

  const handleDrawerOpen = () => {
    setOpen(true);
    setOpenSidenav(dispatch,!openSidenav)
    setTimeout(() => {
      setIsDisplayed(true);
    }, 150);
    setMiniSidenav(dispatch, false);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setOpenSidenav(dispatch,!openSidenav)
    setIsDisplayed(false);
    setMiniSidenav(dispatch, true);
  };
  React.useEffect(() => {
    if (window.innerWidth < breakpoints.values.xl) setOpen(false);
  }, []);
  React.useEffect(() => {
    if (window.innerWidth < breakpoints.values.xl && openLeftSidenav) {
      setOpen(false);
    }
  }, [openLeftSidenav]);
  return (
    <Box role="presentation">
      {!open && (
        <AppBar position="fixed" open={open}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={({ breakpoints }) => ({
                ml: 0.5,
                [breakpoints.down("xl")]: {
                  marginLeft: pxToRem(22),
                  marginTop: pxToRem(4),
                },
              })}
            >
              <MenuSvg />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}
      <Drawer variant="permanent" open={open}>
        <DrawerHeader sx={{ ml: 3 }}>
          {isDisplayed && (
            <Grid
              container
              sx={{
                ...(!open && { display: "none" }),
                width: 240,
              }}
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <MDBox display="flex">
                  {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
                  <MDTypography
                    component="h6"
                    variant="button"
                    fontWeight="medium"
                    color="black"
                    ml={1}
                  >
                    {brandName}
                  </MDTypography>
                </MDBox>
              </Grid>
              <Grid item>
                <IconButton onClick={handleDrawerClose}>{<MenuSvg />}</IconButton>
              </Grid>
            </Grid>
          )}
        </DrawerHeader>
        <MDList color={"primary"} data={routes} open={open} />

        {open ? (
          <MDButton
            href="/authentication/sign-in"
            color="error"
            variant="text"
            startIcon={<ExitIcon />}
            sx={{ position: "absolute", width: "100%", bottom: 24, borderRadius: 0, right: 40 }}
          >
            {t("buttons.exit")}
          </MDButton>
        ) : (
          <IconButton sx={{ position: "absolute", width: "100%", bottom: 24 }}>
            <ExitIcon />
          </IconButton>
        )}
      </Drawer>
    </Box>
  );
}
